<script lang="ts">
  /**
   * Container component for security findings
   *
   * This component acts as a wrapper that coordinates between the PaginationState,
   * PaginationControls, and the DetectedIssuesList component, handling security-findings-specific logic.
   */

  import PaginationControls from "$ui/Pagination/PaginationControls.svelte";
  import FindingFilters, { type FindingFilter } from "./FindingFilters.svelte";
  import { type SecurityFinding } from "$lib/stores/orgSecurityFindings";
  import FindingList from "./FindingList.svelte";
  import { orderBy, sortBy } from "lodash";

  const {
    findings,
    dataFullyLoaded = true,
  }: {
    findings: SecurityFinding[];
    dataFullyLoaded?: boolean;
  } = $props();

  const providers = $derived.by(() => {
    const uniqueProviders = new Set(findings.map((finding) => finding.system));
    return sortBy(Array.from(uniqueProviders), (provider) => provider.toLowerCase());
  });

  let sortField = $state<string | undefined>(undefined);
  let sortDirection = $state<"asc" | "desc">("asc");

  const initialFilters: FindingFilter = {
    status: null,
    topics: null,
    kind: null,
  };

  let filters = $state<FindingFilter>(initialFilters);

  const initialPaginationState = {
    currentPage: 1,
    pageSize: 25,
  };

  let paginationState = $state(initialPaginationState);

  const filteredAndSortedFindings = $derived.by(() => {
    // Only apply filtering and sorting if data is fully loaded
    if (!dataFullyLoaded) {
      return findings;
    }

    const doFiltering =
      filters.status || (filters.topics && filters.topics.length > 0) || filters.kind || filters.provider;
    const doSorting = sortField && sortDirection;

    if (!doFiltering && !doSorting) {
      return findings;
    }

    let filteredAndSortedFindings = findings;

    if (doFiltering) {
      filteredAndSortedFindings = findings.filter((finding) => {
        return (
          (filters.status ? filters.status === finding.status : true) &&
          (filters.topics && filters.topics.length > 0
            ? finding.sensitiveTopics?.some((t) => filters.topics?.includes(t.sensitiveTopic.toLowerCase()))
            : true) &&
          (filters.kind ? filters.kind === finding.securityFindingKind.toLowerCase() : true) &&
          (filters.provider ? finding.system.startsWith(filters.provider) : true)
        );
      });
    }

    if (doSorting && sortField) {
      filteredAndSortedFindings = orderBy(filteredAndSortedFindings, [sortField], [sortDirection]);
    }

    return filteredAndSortedFindings;
  });

  const displayFindings = $derived(
    filteredAndSortedFindings.slice(
      (paginationState.currentPage - 1) * paginationState.pageSize,
      paginationState.currentPage * paginationState.pageSize,
    ),
  );
</script>

<div class="flex flex-col gap-2">
  <FindingFilters
    {findings}
    {filters}
    disabled={!dataFullyLoaded}
    onChange={(newFilters) => {
      filters = newFilters;
      paginationState.currentPage = 1;
    }}
  />

  <PaginationControls
    currentPage={paginationState.currentPage}
    pageSize={paginationState.pageSize}
    pageSizeOptions={[25, 50, 100]}
    totalItems={filteredAndSortedFindings.length}
    onPageChange={(direction: "next" | "prev") => {
      if (direction === "next") {
        paginationState.currentPage = paginationState.currentPage + 1;
      } else {
        paginationState.currentPage = paginationState.currentPage - 1;
      }
    }}
    onPageSizeChange={(newSize: number) => {
      paginationState.pageSize = newSize;
    }}
  />

  <FindingList
    findings={displayFindings}
    {providers}
    {sortField}
    {sortDirection}
    onSort={(newSortField, newSortDirection) => {
      sortField = newSortField;
      sortDirection = newSortDirection;
    }}
  />

  <PaginationControls
    currentPage={paginationState.currentPage}
    pageSize={paginationState.pageSize}
    pageSizeOptions={[25, 50, 100]}
    totalItems={filteredAndSortedFindings.length}
    onPageChange={(direction: "next" | "prev") => {
      if (direction === "next") {
        paginationState.currentPage = paginationState.currentPage + 1;
      } else {
        paginationState.currentPage = paginationState.currentPage - 1;
      }
    }}
    onPageSizeChange={(newSize: number) => {
      paginationState.pageSize = newSize;
    }}
  />
</div>
